import { useEffect, useRef, useState } from "react";
import { PageHero } from "../components/PageShell";
import { ROUTES } from "../lib/routes";
import { AUTONOMOUS_COMMITTEES } from "../lib/autonomousCommittees";

const ALL = "All";

/** Bucket letter for a committee name — its first alphabetic character. */
function letterOf(name: string): string {
  const m = name.match(/[A-Za-z]/);
  return m ? m[0].toUpperCase() : "#";
}

/**
 * Alphabetical display order. The data file deliberately preserves the source
 * page's own ordering for provenance, so the sort lives here in the view.
 */
const SORTED = [...AUTONOMOUS_COMMITTEES].sort((a, b) =>
  a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
);

/** Monogram initials for a roster entry (e.g. "Dr. Dinesh Y N" -> "DY"). */
function initials(name: string): string {
  const words = name
    .replace(/\b(Dr|Prof|Sri|Smt|Mr|Mrs|Ms)\b\.?/gi, " ")
    .replace(/[^A-Za-z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "•";
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Committees — the institute's autonomous committees as an expandable card grid,
 * reproducing the order published at bietdvg.edu/committees. Tap a card to reveal
 * its roster, plus its published objectives for the committees that document them.
 * The statutory compliance committees (Anti-Ragging, ICC, Grievance, …) also have
 * their own treatment on the Compliance page.
 */
export default function Committees() {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [activeLetter, setActiveLetter] = useState<string>(ALL);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /**
   * Opening a card pulls it to the top of the viewport, so its objectives and
   * roster are in view rather than unfolding below the fold. Runs on open only —
   * collapsing, or clearing the open card when the letter filter changes, would
   * otherwise yank the page around for no reason. The header offset comes from
   * `scroll-margin-top` on the card (see detail.css), matching how in-page
   * anchors clear the sticky header elsewhere in the app.
   */
  useEffect(() => {
    if (!openSlug) return;
    const el = cardRefs.current[openSlug];
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [openSlug]);

  const counts = SORTED.reduce<Record<string, number>>((acc, c) => {
    const l = letterOf(c.name);
    acc[l] = (acc[l] ?? 0) + 1;
    return acc;
  }, {});

  const tabs = [
    { key: ALL, label: "All", count: SORTED.length },
    ...Object.keys(counts)
      .sort()
      .map((l) => ({ key: l, label: l, count: counts[l] })),
  ];

  const visible = activeLetter === ALL ? SORTED : SORTED.filter((c) => letterOf(c.name) === activeLetter);

  function selectTab(key: string) {
    setActiveLetter(key);
    // An expanded card in a filtered-out bucket would otherwise linger.
    setOpenSlug(null);
  }

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern).
  function onTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    selectTab(tabs[next].key);
    tabRefs.current[next]?.focus();
  }

  return (
    <>
      <PageHero
        variant="hero-navy"
        eyebrow="Governance · Autonomous committees"
        title="The committees that run an autonomous institute."
        lead="As an autonomous institution, BIET is steered by standing committees and dean portfolios — from Finance and Examinations to IQAC, Placements and Accreditation. Open any committee to see who serves on it."
        crumbs={[{ label: "About", to: ROUTES.about }, { label: "Committees" }]}
        image={null}
      />
      <section className="section">
        <div className="container">
          <div className="kicker">Autonomous committees</div>
          <h2 style={{ marginTop: 8, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>
            Standing committees &amp; dean portfolios.
          </h2>
          <div className="filter-tabs" role="tablist" aria-label="Filter committees by first letter">
            {tabs.map((t, i) => (
              <button
                key={t.key}
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                type="button"
                id={`comm-tab-${i}`}
                aria-selected={activeLetter === t.key}
                aria-controls="comm-panel"
                tabIndex={activeLetter === t.key ? 0 : -1}
                className={
                  "filter-tab" +
                  (t.key === ALL ? "" : " is-letter") +
                  (activeLetter === t.key ? " is-active" : "")
                }
                onClick={() => selectTab(t.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
              >
                {t.label}
                <span className="filter-tab-count">{t.count}</span>
              </button>
            ))}
          </div>

          <div
            id="comm-panel"
            role="tabpanel"
            aria-labelledby={`comm-tab-${tabs.findIndex((t) => t.key === activeLetter)}`}
            className="committee-card-grid"
          >
            {visible.map((c) => {
              const isOpen = openSlug === c.slug;
              const hasObjectives = Boolean(c.objectives?.length);
              const hasTeam = c.team.length > 0;
              const expandable = hasObjectives || hasTeam;
              const toggle = () => expandable && setOpenSlug(isOpen ? null : c.slug);
              return (
                <div
                  key={c.slug}
                  ref={(el) => { cardRefs.current[c.slug] = el; }}
                  className={"gov-committee-card" + (isOpen ? " is-open" : "")}
                >
                  <div
                    className="gcc-head"
                    role={expandable ? "button" : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? isOpen : undefined}
                    onClick={toggle}
                    onKeyDown={(e) => {
                      if (expandable && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                  >
                    <div className="gcc-cat">{c.category}</div>
                    <h3 className="gcc-name">{c.name}</h3>
                    <p className="gcc-purpose">{c.summary}</p>
                    <div className="gcc-foot">
                      {hasTeam && <span className="gcc-lead">{c.team[0]}</span>}
                      <span className="gcc-count">
                        {hasTeam ? `${c.team.length} ${c.team.length === 1 ? "member" : "members"}` : "Roster not published"}
                      </span>
                      {expandable && (
                        <span className="gcc-toggle">
                          {isOpen ? "Hide" : hasObjectives ? "Objectives & team" : "Members"}
                          <i aria-hidden="true">{isOpen ? "−" : "+"}</i>
                        </span>
                      )}
                    </div>
                  </div>

                  {isOpen && expandable && (
                    <div className={"gcc-detail" + (hasObjectives && hasTeam ? "" : " is-single")}>
                      {/* The institute's own preamble, where it publishes one — kept
                          out of the card because its length varies wildly. */}
                      {c.purpose && <p className="gcc-preamble">{c.purpose}</p>}
                      {hasObjectives && (
                        <div className="gcc-col">
                          <h4>Roles &amp; responsibilities</h4>
                          <ol className="gcc-objectives">
                            {c.objectives!.map((o, i) => (
                              <li key={i}>
                                <span className="n">{String(i + 1).padStart(2, "0")}</span>
                                <span className="t">{o}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {hasTeam && (
                        <div className={"gcc-col" + (hasObjectives ? " gcc-col-team" : "")}>
                          <h4>Composition · {c.team.length} {c.team.length === 1 ? "member" : "members"}</h4>
                          <ul className="gcc-roster">
                            {c.team.map((m, i) => (
                              <li key={i}>
                                <span className="gcc-avatar" aria-hidden="true">{initials(m)}</span>
                                <span className="gcc-person">
                                  <span className="name">{m}</span>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
